import { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useState } from 'react';

import { NextButton, PrevButton, usePrevNextButtons } from '~/components/Slider/EmblaSliderArrowButtons';

interface EmblaSliderProps {
  children: React.ReactNode[];
  slideClass?: string;
  className?: string;
  options?: EmblaOptionsType;
  onApiReady?: (_api: EmblaCarouselType) => void;
  showProgress?: boolean;
  /**
   * Trailing margin below the slider, for the usual case of one sitting between page
   * sections. Set false where the slider is the last thing inside its own block and the
   * surrounding element owns the spacing — the margin is a Bootstrap utility, so it
   * cannot be overridden from a stylesheet without `!important`.
   */
  spacing?: boolean;
}

const EmblaSlider = ({
  children,
  slideClass,
  className,
  options,
  onApiReady,
  showProgress = true,
  spacing = true,
}: EmblaSliderProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(options);
  const [scrollProgress, setScrollProgress] = useState(0);
  // Becomes true once Embla has initialized and positioned the slides. Until
  // then the viewport is pinned to the first slide via CSS so the pre-hydration
  // paint never flashes a restored/half-aligned scroll position.
  const [isReady, setIsReady] = useState(false);

  const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } = usePrevNextButtons(emblaApi);

  const onScroll = useCallback(
    (api: EmblaCarouselType) => {
      if (!showProgress) return;
      const progress = Math.max(0, Math.min(1, api.scrollProgress()));
      setScrollProgress(progress * 100);
    },
    [showProgress]
  );

  useEffect(() => {
    if (!emblaApi) return;

    onApiReady?.(emblaApi);
    setIsReady(true);

    if (showProgress) {
      onScroll(emblaApi);
      emblaApi.on('reInit', onScroll).on('scroll', onScroll).on('slideFocus', onScroll);
    }
  }, [emblaApi, onApiReady, onScroll, showProgress]);

  const isStatic = prevBtnDisabled && nextBtnDisabled;

  useEffect(() => {
    emblaApi?.reInit();
  }, [emblaApi, isStatic]);

  return (
    <div
      className={`embla embla--main${spacing ? ' mb-4' : ''}${isStatic ? ' embla--static' : ''}${isReady ? ' embla--ready' : ''}${className ? ` ${className}` : ''}`}
    >
      <div className="embla__viewport" ref={emblaRef}>
        <div className="embla__container">
          {children.map((child, index) => (
            <div className={`embla__slide ${slideClass ?? ''}`} key={index}>
              <div className="embla__slide__inner">{child}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="embla__controls">
        {showProgress && (
          <div className="embla__progress">
            <div className="embla__progress__bar" style={{ transform: `translate3d(${scrollProgress}%,0,0)` }} />
          </div>
        )}

        <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} />
        <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} />
      </div>
    </div>
  );
};

export default EmblaSlider;
